// Drexel University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Drexel's Common Data Set 2025-2026 (Office of Institutional Research,
//    Assessment and Accreditation PDF): C1 applicants, admits and enrollees
//    for Fall 2025 with the residency table, C8 test policy for Fall 2027
//    and the score date, C21/C22 plans with the early decision counts for
//    the Fall 2025 entering class (no early action counts are given).
//  - drexel.edu admissions: First-Year Application Deadlines; Early
//    Decision, Early Action, and Regular Decision Options; Standardized
//    Testing Policies. The deadlines page carries no year label; it is the
//    page as posted for the application cycle open now (Fall 2027 entry).
//  - Drexel Central, Cost of Attendance for Undergraduate Programs: the
//    2026-2027 on-campus budget for three terms. The CDS G1/G5 give the same
//    2026-2027 tuition, fees, housing, food, books, transportation figures;
//    Drexel Central's budget is used because it itemizes every charge.
//  - College Scorecard API, school id 212054 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Drexel Common Data Set 2025-2026',
        url: 'https://drexel.edu/institutionalresearch/~/media/Drexel/Provost-Group/InstitutionalResearch/Documents/Factbook/CDS_2025-2026.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Drexel Admissions, First-Year Application Deadlines',
        url: 'https://drexel.edu/admissions/apply/undergrad-instructions/first-year-instructions/application-deadlines',
        read: '2026-10-01',
    },
    plans: {
        label: 'Drexel Admissions, Early Decision, Early Action, and Regular Decision Options',
        url: 'https://drexel.edu/admissions/apply/undergrad-instructions/first-year-instructions/early-decision-early-action',
        read: '2026-10-01',
    },
    testing: {
        label: 'Drexel Admissions, Standardized Testing Policies',
        url: 'https://drexel.edu/admissions/apply/undergrad-instructions/first-year-instructions/standardized-tests',
        read: '2026-10-01',
    },
    coa: {
        label: 'Drexel Central, Cost of Attendance for Undergraduate Programs',
        url: 'https://drexel.edu/drexelcentral/cost/undergrad',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?212054-Drexel-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'drexel-university',
    kbId: 'drexel_university',
    name: 'Drexel University',
    shortName: 'Drexel',
    location: 'Philadelphia, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Drexel identity, colors); decoration only.
    branding: {
        primary: '#07294D',
        secondary: '#FFC600',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/drexel_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 38030,
        admitted: 26583,
        enrolled: 1948,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10005, admitted: 6899 },
            { group: 'Out-of-state', applied: 19173, admitted: 14600 },
            { group: 'International', applied: 8852, admitted: 5084 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Drexel\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts but none for early action, which is not binding.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 299, admitted: 234 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 15', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 15', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'The application and every required document must arrive by the deadline; it is a received-by date, not a postmark. The CSS Profile is due November 25 for Early Decision and Early Action and February 1 for Regular Decision. Admitted Early Decision students pay the tuition and housing deposits by January 15; everyone else by May 1. Applicants to the BA/BS+MD Early Assurance program may apply only Early Action or Regular Decision, with all documents in by November 15.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if admitted, you attend Drexel and withdraw your other applications, and you confirm by January 15. Drexel releases you if the aid offer is not affordable for your family. Early Action is not binding; you have until May 1.',
        bindingSource: 'plans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Drexel is No Harm Test-Optional: you choose whether to send SAT or ACT scores, and scores that would hurt your application are not used. The one exception is the BA/BS+MD Early Assurance program, which requires the SAT or ACT. Applying without scores does not affect merit scholarships.',
        source: 'testing',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 63078 },
            { item: 'Fees', amount: 2370 },
            { item: 'Immunization fee (one time)', amount: 50 },
            { item: 'Housing (on campus)', amount: 12045 },
            { item: 'Food', amount: 7353 },
            { item: 'Books', amount: 1200 },
            { item: 'Computer (first term)', amount: 500 },
            { item: 'Transportation', amount: 900 },
            { item: 'Personal', amount: 600 },
            { item: 'Loan origination fee', amount: 60 },
        ],
        total: 88156,
        note: 'For three terms (fall, winter and spring) living on campus. The loan origination fee is Drexel\'s estimate for students who borrow Federal Direct Loans. Housing varies by residence hall.',
        source: 'coa',
        netPrice: {
            average: 37529,
            byIncome: [
                { income: '$0 to $30,000', amount: 28843 },
                { income: '$30,001 to $48,000', amount: 28147 },
                { income: '$48,001 to $75,000', amount: 31498 },
                { income: '$75,001 to $110,000', amount: 38569 },
                { income: 'Over $110,000', amount: 46936 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Drexel\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
